<template>
  <div class="commerce-order">
    <div v-if="$store.state.user">
      <MainBackButton v-if="showBackButton" />
    </div>
    <div v-else>
      <span class="generic-order">
        <i class="fa fa-shopping-basket" />
      </span>
    </div>

    <div v-if="t">
      <slide-y-down-transition>
        <CommerceOrderHelpWall v-if="showHelp" :t="t" :fullRef="ref" @close="showHelp = false" />
      </slide-y-down-transition>

      <slide-y-down-transition>
        <CommerceOrderDeliveryWall v-if="showDelivery" :t="t.delivery" :z="z" :notes="deliveryNotes"
          :businessRef="`${t.data.business.name} #${t.data.ref}`" @callShop="callShop" @triggerHelp="triggerHelp"
          @update="init" @close="showDelivery = false" />
      </slide-y-down-transition>

      <CommerceOrderPayment v-if="showPayment" :bankAccount="bankAccount" :promptPay="promptPay" :currency="currency"
        :amount="orderValue" :orderID="t.id" :t="t" @update="init" @close="showPayment = false" />
    </div>

    <div v-if="t" class="wrapper no-scrollbar" :key="z">
      <div class="is-pulled-right">
        <span v-if="isTest" class="test-order" @click="manageTestOrder">
          Manage Test Order
          <i class="fa fa-external-link-alt fa-sm" style="margin-left: 3px" />
        </span>
        <span v-else style="padding-left: 20px">
          <i v-if="updating" class="fa fa-spinner fa-spin fa-lg has-text-info" />
          <i v-else class="fa fa-share-alt fa-lg has-text-grey" @click="shareOrder" />
        </span>
      </div>
      <p class="header" :class="{ 'has-text-danger': t.is_deleted }" @click="init">
        {{ $t("commerce.order-details") }}
      </p>

      <div class="has-text-centered" style="padding-bottom: 20px; color: #444" @click="init">
        <!-- <p style="padding-bottom: 10px"><i class="far fa-check-circle fa-8x" /></p> -->
        <div v-if="simpleState === 'placed'">
          <!-- <p class="state-icon">🎉</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/order-submitted.json')" :loop="false" />
          </div>
          <p>{{ $t("commerce-order.your-order-has-been-placed") }}</p>
        </div>
        <div v-else-if="simpleState === 'being-prepared'">
          <!-- <p class="state-icon">👩🏻‍🍳</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/order-prepared.json')" :loop="true" />
          </div>
          <p>{{ $t("commerce-order.your-order-is-being-prepared") }}</p>
          <p v-if="isDelivery">
            {{ $t("commerce-order.will-notify-when-delivery-on-the-way") }}
          </p>
          <p v-if="isTakeaway">
            We'll notify you when your order is ready for collection
          </p>
        </div>
        <div v-else-if="simpleState === 'ready-for-pickup'">
          <!-- <p class="state-icon">🚶🏻‍♀️</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/ready-for-pickup.json')" :loop="false" />
          </div>
          <p>{{ $t("commerce-order.your-order-is-ready-for-pickup") }}</p>
        </div>
        <div v-else-if="simpleState === 'on-the-way'">
          <!-- <p class="state-icon" style="transform: scale(-1, 1)">🛺</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/on-the-way.json')" :loop="true" />
          </div>
          <p>{{ $t("commerce-order.your-order-is-on-the-way") }}</p>
        </div>
        <div v-else-if="simpleState === 'customer-cancelled'">
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/order-cancelled.json')" :loop="false" />
          </div>
          <p class="bg-pink">{{ $t("commerce-order.you-cancelled-order") }}</p>
        </div>
        <div v-else-if="simpleState === 'shop-cancelled'">
          <!-- <p class="state-icon">😢</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/order-cancelled.json')" :loop="false" />
          </div>
          <p style="font-weight: 500">
            {{ $t("commerce-order.sorry-shop-cancelled-order") }}
          </p>
        </div>
        <div v-else-if="simpleState === 'timed-out'">
          <!-- <p class="state-icon">⌛️</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/time-out.json')" :loop="false" />
          </div>
          <p>{{ $t("commerce-order.sorry-shop-didnt-accept") }}</p>
          <p class="timeout-option" @click="callShop">
            <i class="fa fa-phone has-text-link is-pulled-right" style="padding: 5px" />
            You can try calling them to check if they're open and forgot to
            accept your order.
          </p>
          <p class="timeout-option" @click="goToEat">
            <i class="fa fa-chevron-right fa-lg has-text-success is-pulled-right" style="padding: 5px" />
            Or you can order from another shop that's open now.
          </p>
        </div>
        <div v-else>
          <!-- <p class="state-icon">🙏🏼</p> -->
          <div class="lottie">
            <lottie-animation :animationData="require('@/assets/lottie/dropped-off.json')" :loop="true" />
          </div>
          <p>{{ $t("commerce-order.your-order-is-completed") }}</p>
        </div>
      </div>

      <div v-if="showBars" class="columns is-mobile state-bars">
        <div class="column" style="padding-left: 0">
          <progress v-if="simpleState === 'placed'" class="progress is-small is-success" max="1" />
          <progress v-else class="progress is-small is-success" value="1" />
        </div>
        <div class="column">
          <progress v-if="simpleState === 'being-prepared'" class="progress is-small is-success" max="1" />
          <progress v-else-if="simpleState === 'placed'" class="progress is-small is-success" value="0" />
          <progress v-else class="progress is-small is-success" value="1" />
        </div>
        <div class="column" style="padding-right: 0">
          <progress v-if="simpleState === 'on-the-way' || simpleState === 'ready-for-pickup'
            " class="progress is-small is-success" max="1" />
          <progress v-else-if="simpleState === 'done'" class="progress is-small is-success" value="1" />
          <progress v-else class="progress is-small is-success" value="0" />
        </div>
      </div>

      <div v-if="showBars">
        <div v-if="t.state === 'initiated'" class="call-shop-prompt" @click="callShop">
          <p>
            This shop usually confirms within 5 minutes. If they haven't you can
            call them and say:
            <b>I ordered on TUK #{{ this.ref }}. Can you please confirm.</b>
          </p>
          <div style="height: 6px" />
          <p class="button is-link is-outlined is-fullwidth" style="font-weight: 500">
            Call now&nbsp;<i class="fa fa-chevron-right has-text-link" />
          </p>
        </div>
        <div v-if="tukDiscount" class="call-shop-prompt blue">
          <p><b>TUK Discount</b> has been added to your order (see below)</p>
        </div>
        <div v-if="hasOutOfStock" class="call-shop-prompt yellow" @click="callShop">
          <p>
            The shop ammended your order because an item was
            <b>Out of Stock</b> (see below). Please call the shop if you'd like
            to add an item to replace it.
          </p>
          <div style="height: 6px" />
          <p class="button is-link is-outlined is-fullwidth" style="font-weight: 500">
            Call now&nbsp;<i class="fa fa-chevron-right has-text-link" />
          </p>
        </div>
        <div v-if="showDeliveryMapButton" class="button is-fullwidth is-link is-outlined map-btn"
          @click="showDelivery = true">
          {{ $t("commerce-order.view-delivery-on-map") }}&nbsp;
          <i class="fa fa-chevron-right" />
        </div>

        <div v-if="showDirectionsToShopButton" class="button is-fullwidth is-link is-outlined map-btn"
          @click="openDirectionsToShop">
          {{ $t("commerce-order.view-directions-to-the-shop") }}&nbsp;
          <i class="fa fa-chevron-right" />
        </div>

        <div v-if="showPaymentButton" class="button is-fullwidth is-white is-outlined pay-btn"
          @click="showPayment = true">
          <span>{{ $t("payment.pay-now") }}</span>
          <!-- <span v-if="isPromptPay">{{
            $t("commerce-order.promptpay-details")
          }}</span>
          <span v-else-if="isBankTransfer">{{
            $t("commerce-order.bank-transfer-details")
          }}</span>
          <span v-else>{{
            $t("commerce-order.contactless-payment-options")
          }}</span> -->
          <i class="fa fa-chevron-right" style="margin-left: 6px" />
        </div>
        <div v-else-if="hidePaymentButton" class="button is-fullwidth is-success is-outlined"
          style="margin-bottom: 20px; font-weight: 500" @click="paymentCompleteInfo">
          {{ $t("commerce-order.payment-complete") }}
        </div>
        <div v-else-if="t.is_ongoing" class="button is-fullwidth has-text-grey" style="margin-bottom: 20px"
          @click="paymentPendingInfo">
          {{ $t("commerce-order.payment-pending") }}
          <i class="fa fa-spinner fa-spin" style="margin-left: 6px" />
        </div>

        <div class="button is-fullwidth" style="margin-bottom: 20px" @click="triggerHelp">
          <i>{{ $t("commerce-order.need-help-with-order-?") }}</i>
        </div>
      </div>
      <div v-else class="fail-divider" />

      <zoom-y-transition>
        <div v-if="simpleState === 'shop-cancelled' && loadingTimer > 1" class="call-shop-prompt blue"
          @click="gotoAlternativeShop">
          <i class="fa fa-info-circle has-text-info" />&nbsp;
          <span v-if="t.data.alternative_shop">Here's a similar top-rated alternative you might consider</span>
          <span v-else>Here are some top-rated alternatives that are open now</span>
          &nbsp;<i class="fa fa-chevron-right has-text-info" />
        </div>
      </zoom-y-transition>

      <div style="margin: 20px 0">
        <span class="button is-link is-outlined" @click="callShop">
          {{ $t("call-the-shop") }}&nbsp;<i class="fa fa-phone fa-sm" />
        </span>

        <span class="button is-white has-text-black is-pulled-right" @click="copyOrderID">
          {{ "Order" }}&nbsp;
          <span class="has-text-weight-medium">#{{ ref }}</span>
        </span>
      </div>

      <p class="notification has-text-weight-semibold" style="padding: 10px" @click="goToShop">
        {{ t.business.name }}
      </p>

      <div class="transaction" style="margin-top: 1px">
        <div class="columns is-mobile is-vcentered">
          <div class="column is-narrow" @click="goToShop">
            <img v-webp="businessProfilePic" @error="defaultImg" />
          </div>
          <div class="column has-text-left" style="padding: 0; margin: 0; padding-left: 0">
            <p>
              <i class="fa fa-sm pad" :class="fulfilmentIcon(t)" />
              <span class="has-text-weight-medium">{{
                fulfilmentType(t)
              }}</span>
              <span v-if="contactlessNote" class="has-text-info"
                style="font-weight: 500; font-size: 0.8em; margin-left: 6px">Contactless</span>
              <span v-if="t.data.order.fulfilment_time" class="has-text-primary"
                style="font-weight: 500; margin-left: 6px">@ {{ t.data.order.fulfilment_time }}</span>
              <span v-if="hasDeliveryPhoto" class="is-pulled-right link has-text-link" style="margin-right: 10px"
                @click="showDeliveryPhoto">
                <i class="far fa-image" />&nbsp;Photo
              </span>
            </p>
            <p>
              <i class="fa fa-coins fa-sm pad" />
              <span>{{ currency }}{{ orderValue }}</span>
              <span v-if="isSOL" class="total-sol">{{ orderValueSOL }} SOL</span>
              <span v-else class="has-text-grey is-uppercase payment">{{
                translatePayment(t.data.order.payment_method)
              }}</span>
              <span v-if="t.data.payment_receipt_pic" class="has-text-success has-text-weight-medium link"
                @click="paymentReceiptConfirmation">
                <i class="far fa-check-circle" />&nbsp;PAID
              </span>
              <i v-if="isCardPayment">
                <i class="far fa-check-circle has-text-success" />&nbsp;PAID</i>
            </p>
            <p>
              <i class="far fa-clock fa-sm pad" />{{ $t("ecommerce.ordered") }}
              <span v-if="hoursAgo(t.created_at) < 12">
                {{ hhmmTime(t.created_at) }}
              </span>
              <span v-else>
                {{ longScheduledTime(t) }}
                <!-- {{ minutesAgo(t.created_at) }} -->
              </span>
            </p>
          </div>
        </div>
      </div>

      <div>
        <table class="table is-fullwidth" style="margin-bottom: 0">
          <thead>
            <tr>
              <th style="padding-right: 0">
                <!-- <i>{{ $t("ecommerce.item") }}</i> -->
                <i class="fa fa-camera-retro more-btn" @click="toggleItemsInfo" />
                <span class="buttons is-uppercase" style="display: inline-block; margin-top: -2px">
                  <span v-for="(l, i) in languages" :key="i" class="
                      button
                      is-small is-rounded
                      has-text-weight-medium
                      is-uppercase
                    " :class="{ 'is-white has-text-grey': lang !== l }" style="padding: 0 4px; margin: 0"
                    @click="lang = l">{{ l }}</span>
                </span>
              </th>
              <th class="is-narrow">
                {{ $t("ecommerce.price") }}
              </th>
              <th class="is-narrow">
                {{ $t("ecommerce.qty") }}
              </th>
            </tr>
          </thead>
          <tbody v-for="(o, i) in basketItems" :key="i" class="items">
            <tr :class="{
              'no-bottom-border': o.item.options,
              'special-item-positive':
                isSpecialItem(o) && discountedTotalPrice(o.item) >= 0,
              'special-item-negative':
                isSpecialItem(o) && discountedTotalPrice(o.item) < 0,
              'oos-item': isOriginalOOS(o),
            }" @click="console(o)">
              <td style="padding-right: 0">
                <span v-if="o.item.free_gift" class="has-text-danger"
                  style="font-size: 0.8em; font-weight: 500; opacity: 0.8">
                  <i class="fa fa-gift" />&nbsp;Free Gift
                </span>
                <span v-if="isSpecialItem(o)">*</span>{{ itemName(o.item)
                }}<span v-if="isSpecialItem(o)">*</span>
                <i v-show="o.item.discount" class="fa fa-tag has-text-danger discount-tag" />
                <p v-if="o.out_of_stock" class="out-of-stock">
                  {{ $t("commerce.out-of-stock") }}: {{ o.out_of_stock }}
                  <span style="opacity:0.8">of {{ o.quantity }}</span>
                </p>

                <div class="is-flex" style="background-color: lightyellow">
                  <p v-if="showPics && o.item.pic" style="flex-shrink: 1">
                    <img class="item-img" v-webp="o.item.pic || placeholderPic"
                      :class="{ 'contain-img': o.item.contain_image }" />
                  </p>
                  <p v-if="showDescriptions && itemDescription(o.item)" class="description" style="flex: 1">
                    {{ itemDescription(o.item) }}
                  </p>
                </div>
              </td>
              <td class="nowrap">
                <span v-if="actualQuantity(o) === 0"><i class="fa fa-minus has-text-grey" /></span>
                <span v-else-if="discountedTotalPrice(o.item) >= 0">{{ currency }}{{ discountedTotalPrice(o.item)
                }}</span>
                <span v-else>−{{ currency
                }}{{ Math.abs(discountedTotalPrice(o.item)) }}</span>
              </td>

              <td :class="{ 'has-text-danger': o.out_of_stock }">
                <!-- <span v-if="isOOS(o)">−</span> -->
                {{ actualQuantity(o) }}
              </td>
            </tr>
            <tr v-if="hasOptions(o) || o.item.comment || o.item.by_weight" :class="{ 'oos-item': isOriginalOOS(o) }">
              <td colspan="3" class="td-options">
                <div class="options">
                  <p v-if="o.item.by_weight" class="has-text-weight-medium">
                    &nbsp;<i class="fa fa-weight fa-sm" />&nbsp;
                    <span v-if="o.weighed">
                      {{ o.item.actual_weight }}
                      grams per piece *weighed*
                    </span>
                    <span v-else>
                      ~{{ o.item.estimated_weight }}
                      grams per piece
                      <!-- *to be weighed* -->
                    </span>
                  </p>
                  <p v-for="o in o.item.options" :key="o.item">
                    <span class="option-menu">&nbsp;•&nbsp;{{ optionMenuName(o) }}
                    </span>
                    <span v-if="o.option.quantity > 1">{{ o.option.quantity }} x</span>
                    {{ optionItemName(o) }}
                  </p>
                  <p v-if="o.item.comment">
                    &nbsp;<i class="far fa-comment option-menu" />
                    <i style="margin-left: 0px">
                      {{ o.item.comment }}
                    </i>
                  </p>
                </div>
              </td>
            </tr>
            <tr v-else-if="!isSpecialItem(o)" style="height: 6px" />
          </tbody>
          <tbody>
            <tr v-if="specialDiscount" class="has-text-info">
              <td class="wide">
                {{ $t("Cash Back")
                }}<i class="fa fa-tag has-text-danger discount-tag" />
              </td>
              <td colspan="1" class="has-text-weight-medium nowrap">
                −{{ currency }}{{ specialDiscount }}
              </td>
              <td></td>
            </tr>
            <tr v-if="fulfilmentDiscount" class="has-text-info">
              <td class="wide">{{ fulfilmentText }} Discount</td>
              <td class="nowrap">−{{ currency }}{{ fulfilmentDiscount }}</td>
              <td></td>
            </tr>
            <tr v-if="vat" class="has-text-grey">
              <td>{{ $t("VAT") }}</td>
              <td>{{ currency }}{{ vat }}</td>
              <td></td>
            </tr>
            <tr v-if="showDeliveryFee" class="has-text-grey">
              <td>{{ $t("delivery.delivery-fee") }}</td>
              <td>{{ currency }}{{ deliveryFee }}</td>
              <td></td>
            </tr>
            <tr v-if="tukDiscount" class="has-text-info has-text-weight-medium">
              <td class="wide">
                TUK Discount<i class="fa fa-tag has-text-danger discount-tag" />
              </td>
              <td class="nowrap">−{{ currency }}{{ tukDiscount }}</td>
              <td></td>
            </tr>
          </tbody>
          <tbody style="background-color: rgb(245, 245, 245)">
            <tr>
              <th>{{ $t("ecommerce.total") }}</th>
              <th>{{ currency }}{{ orderValue }}</th>
              <th>{{ itemsCount }}</th>
            </tr>
          </tbody>
        </table>

        <div v-if="cashback" class="notes transaction"
          style="background-color: lightyellow; padding: 6px; margin-top: 9px" @click="showCashbackInfo">
          <i class="has-text-link">{{ $t("promos.cashback-promo") }}</i>
          <span class="is-pulled-right label">{{ youGetXCashback }}
            <i class="fa fa-info-circle" style="margin-left: 4px; color: #bbb" />
          </span>
        </div>

        <div v-show="orderNotes" class="notes has-text-grey">
          <i class="far fa-comment-alt fa-sm" style="margin: 4px" />
          <i>{{ $t("ecommerce.notes") }}:</i>&nbsp;
          <span class="has-text-weight-medium">{{ orderNotes }}</span>
        </div>

        <div v-if="contactlessNote" class="notes has-text-grey">
          <i class="far fa-check-circle fa-sm" style="margin: 4px" />
          <i>{{ $t("Contactless") }}:</i>&nbsp;
          <span class="has-text-weight-medium">{{ contactlessNote }}</span>
        </div>

        <div v-show="deliveryNotes" class="notes has-text-grey">
          <i class="fa fa-map-marker-alt fa-sm" style="margin: 4px" />
          <i>{{ $t("address.delivery") }}:</i>&nbsp;
          <span class="has-text-weight-medium">{{ deliveryNotes }}</span>
        </div>
      </div>
      <div style="margin-top: 20px">
        <span class="link has-text-weight-medium" style="margin-left: 4px">
          <span v-if="t.state === 'initiated'" class="has-text-danger" @click="cancelOrder">
            {{ $t("ecommerce.cancel-order") }}
          </span>
          <span v-else class="has-text-link" @click="callShop">
            {{ $t("call-the-shop") }}
          </span>
        </span>
        <!-- <span
              v-else-if="t.state === 'timed_out'"
              class="button is-rounded is-warning is-small"
              @click="restartOrder"
            >Restart</span>-->

        <span class="is-pulled-right" @click="copyOrderID">
          {{ $t("ecommerce.order-id") }}
          <span class="has-text-weight-medium">#{{ ref }}</span>
          <span style="
              background-color: #ffe;
              color: #777;
              border-radius: 2px;
              font-size: 0.8em;
              padding: 0 3px;
              margin-left: 6px;
            ">{{ orderID(t) }}</span>
        </span>
      </div>

      <div class="faq">
        <p class="list-title">
          <i>{{ $t("faq.frequently-asked-questions") }}</i>
        </p>
        <div class="list">
          <i class="list-item" @click="faqIsFoodReady">{{
            $t("faq.is-my-order-ready-?")
          }}</i>
          <i v-if="!isNotDelivery" class="list-item" @click="faqWhereIsDriver">{{ $t("faq.where-is-driver-?") }}</i>
          <i v-if="isNotDelivery" class="list-item" @click="faqHowDoIPayNotDelivery">{{ $t("faq.how-do-i-pay-?") }}</i>
          <i v-if="isNotDelivery" class="list-item" @click="faqWhatIsShopAddress">{{ $t("faq.what-is-shop-address-?")
          }}</i>
          <i class="list-item" @click="faqIncorrectOrder">{{
            $t("faq.what-if-incorrect-order-?")
          }}</i>
          <i class="list-item" @click="faqCancelOrder">{{
            $t("faq.cancel-order-?")
          }}</i>
        </div>
      </div>
    </div>

    <div v-else class="wrapper">
      <!-- <div class="wrapper"> -->
      <p class="header">
        {{ $t("commerce-order.loading-order-details") }}
      </p>
      <div>
        <div style="height: 20px" />
        <p class="image is-128x128" style="margin: auto">
          <b-skeleton circle width="128px" height="128px"></b-skeleton>
        </p>

        <p style="padding-top: 20px; width: 200px; margin: auto">
          <b-skeleton></b-skeleton>
        </p>
        <div style="height: 20px" />

        <div class="columns is-mobile state-bars">
          <div class="column" style="padding-left: 0">
            <progress class="progress is-small is-white" max="1" />
          </div>
          <div class="column" style="padding-left: 0">
            <progress class="progress is-small is-white" max="1" />
          </div>
          <div class="column" style="padding-left: 0">
            <progress class="progress is-small is-white" max="1" />
          </div>
        </div>

        <div style="height: 10px" />
        <b-skeleton height="30px"></b-skeleton>

        <div style="height: 20px" />
        <b-skeleton height="30px"></b-skeleton>

        <div style="height: 20px" />
        <b-skeleton width="250px"></b-skeleton>

        <div style="height: 20px" />
        <article class="media">
          <figure class="media-left">
            <p class="image">
              <b-skeleton circle width="80px" height="80px"></b-skeleton>
            </p>
          </figure>
          <div class="media-content">
            <div class="content">
              <p>
                <!-- <b-skeleton active></b-skeleton> -->
                <b-skeleton height="80px"></b-skeleton>
              </p>
            </div>
          </div>
        </article>

        <div style="height: 20px" />
        <b-skeleton height="200px"></b-skeleton>
      </div>

      <div v-if="loadingTimer > 10" class="blocked-wrapper">
        <p class="has-text-centered">
          <i class="fa fa-wifi fa-2x has-text-danger" />
        </p>
        <p>Oops, it seems your browser is blocking the order from loading.</p>
        <p>
          Please copy the order link and paste it into another browser such as:
        </p>

        <p>
          <i class="fab fa-chrome" />&nbsp;<b>Chrome</b> or
          <i class="fab fa-safari" />&nbsp;<b>Safari</b>
        </p>
        <p>
          <span class="button is-fullwidth is-medium is-link" @click="copyURL"><i class="fa fa-copy" />&nbsp;Click to Copy
            Order URL</span>
        </p>
      </div>
    </div>
  </div>
</template>


<script>
// import EventBus from "../eventBus";
import MainBackButton from "@/components/MainBackButton";
import CommerceOrderHelpWall from "@/components/CommerceOrderHelpWall";
import CommerceOrderDeliveryWall from "@/components/CommerceOrderDeliveryWall";
import CommerceOrderPayment from "@/components/CommerceOrderPayment";

import LottieAnimation from "lottie-web-vue"; // import lottie-web-vue

export default {
  name: "CommerceOrder",
  components: {
    MainBackButton,
    CommerceOrderHelpWall,
    CommerceOrderDeliveryWall,
    CommerceOrderPayment,
    LottieAnimation,
  },
  data() {
    return {
      updating: false,
      transaction: null,
      lang: this.$store.state.language,
      showDescriptions: false,
      showPics: false,
      placeholderPic: this.$store.state.menuItemPlaceholderPic,
      showHelp: false,
      showDelivery: false,
      showPayment: false,
      // startLoadingAt: new Date(),
      loadingTimer: 0, // seconds
      timer: null, // setTimeout to update order every 60 seconds
      z: 0, // increment on order update
    };
  },
  computed: {
    ref() {
      var name = this.t.data.business.name.replace(/[^A-Z0-9]/gi, "");
      var prefix = name.slice(0, 3).toUpperCase();
      if (this.t.data.ref_prefix) {
        return `${this.t.data.ref_prefix}-${prefix}${this.t.data.ref}`;
      }
      return `${prefix}${this.t.data.ref}`;
    },
    hasDeliveryPhoto() {
      if (this.t.data.type !== "delivery") return null;
      if (!this.t.delivery) return null;
      return this.t.delivery.data.delivery_confirmation_pic;
    },
    // loadingTimer() {
    // var now = new Date();
    // return (now.getTime() - this.startLoadingAt.getTime()) / 1000; // seconds
    // },
    isSOL() {
      return this.t.data.order.payment_method === "sol";
    },
    showBackButton() {
      if (this.showHelp || this.showDelivery) return false;
      return true;
    },
    isTest() {
      var notes = this.t.data.order.basket.notes;
      if (!notes) return false;
      return notes.includes("TEST ORDER");
    },
    fulfilmentText() {
      return this.$t(`commerce.${this.t.data.type}`);
    },
    isCardPayment() {
      return this.t.data.order.payment_method === "card";
    },
    isTakeaway() {
      return this.t.data.type === "take-away";
    },
    isNotDelivery() {
      return this.t.data.type !== "delivery";
    },
    isDelivery() {
      // return true only if delivery has a driver assigned..
      // return t.delivery; // doesn't show because backend isn't setting delivery transaction
      if (this.t.data.type !== "delivery") return false;
      // check if ongoing transactions has a delivery with this commerce transactions id
      var txns = this.$store.state.ongoingCustomerTransactions;
      if (!txns) return false; // should use getters to guarantee array []
      var d = txns.find(
        (txn) => txn.data.commerce_transaction_id === this.t.id
      ); // this is the delivery txn
      if (!d) return false; // no ongoing delivery txn
      if (d.state === "initiated") return false; // delivery txn, but no driver yet...
      return true;
    },
    showDirectionsToShopButton() {
      return this.t.data.type !== "delivery";
    },
    showDeliveryMapButton() {
      return this.t.delivery;
      // return this.t.data.type === "delivery";
      // if (!this.isDelivery) return false;
      // var states = ["enroute", "alighted"];
      // if (states.includes(this.t.delivery.state)) return true;
      // return false;
    },
    showPaymentButton() {
      if (!this.t.delivery) return false;
      // if (!this.t.delivery.is_ongoing) return false;
      if (!this.deliveryRecentlyOngoing) return false;
      return this.promptPay || this.bankAccount;
    },
    hidePaymentButton() {
      if (this.isCardPayment) return true;
      if (this.t.delivery) {
        if (this.t.delivery.is_ongoing) return false; // delivery ongoing
        return this.hoursAgo(this.t.delivery.updated_at) > 2;
      }
      if (this.t.is_ongoing) return false; // commerce order ongoing
      return this.hoursAgo(this.t.updated_at) > 2;
    },
    deliveryRecentlyOngoing() {
      if (!this.t.delivery) return false;
      if (this.t.delivery.state === "confirmed") return false;
      if (this.t.delivery.is_ongoing) return true;
      return this.hoursAgo(this.t.updated_at) < 2; // or use this.t.delivery.updated_at ??
    },
    driver() {
      if (!this.t.delivery) return null;
      return this.t.delivery.driver;
    },
    promptPay() {
      if (!this.driver) return null;
      return this.driver.role.data.qr_pic_url;
    },
    bankAccount() {
      if (!this.driver) return null;
      return this.driver.role.data.bank_account;
    },
    isPromptPay() {
      return this.t.data.order.payment_method === "promptpay";
    },
    isBankTransfer() {
      return this.t.data.order.payment_method === "bank_transfer";
    },
    showBars() {
      if (this.t.state === "cancelled_by_shop") return false;
      if (this.t.state === "timed_out") return false;
      return true;
    },
    businessProfilePic() {
      var fallback = "https://i.ibb.co/DK0f6y3/TUK-Esssentials-profile.png"; // default profile photo
      return this.t.business.profile_pic_url || fallback;
    },
    youGetXCashback() {
      var amount = `${this.currency}${this.cashback}`;
      return this.$t("promos.you-get-x", { x: amount });
    },
    picsStyle() {
      if (this.showPics) return `background-color:yellow`;
      return null;
    },
    descriptionStyle() {
      if (this.showDescriptions) return `background-color:yellow`;
      return null;
    },
    t() {
      // return null;
      return this.transaction;
    },
    basketItems() {
      return this.t.data.order.basket.items;
    },
    languages() {
      return this.t.data.order.basket.languages || ["en", "th"]; // new way
      // // check languages object on first item to get languages set to true
      // if (this.basketItems.length === 0) return [];
      // var obj = this.basketItems[0].item.languages;
      // if (!obj) return [];
      // return Object.keys(obj).filter((k) => {
      //   return obj[k];
      // });
    },
    cashback() {
      return this.t.data.order.cashback;
    },
    orderNotes() {
      return this.t.data.order.basket.notes;
    },
    contactlessNote() {
      if (!this.t.data.delivery) return false;
      return this.t.data.delivery.contactless_note;
    },
    deliveryNotes() {
      if (!this.t.data.address) return null;
      return this.t.data.address.notes;
    },
    itemsCount() {
      var cnt = 0;
      this.basketItems.forEach((o) => (cnt += this.actualQuantity(o)));
      // this.basketItems.forEach((o) => {
      //   if (this.isOOS(o)) cnt -= o.quantity;
      //   else cnt += o.quantity;
      // });
      return cnt;
    },
    // deprecated isCustomer
    isCustomer() {
      if (this.t.created_by === this.userID()) return true;
      return false;
    },
    // this should not be less than 0
    orderValue() {
      // add fees and discounts
      var fees = this.vat;
      var discounts =
        this.specialDiscount + this.fulfilmentDiscount + this.tukDiscount;
      var orderValue = this.t.data.order.order_value + fees - discounts;
      if (this.t.data.type !== "delivery") return orderValue;
      if (this.t.data.delivery.is_free_delivery) return orderValue;
      var deliveryFee = this.subsidisedDeliveryFee;
      return orderValue + deliveryFee;
    },
    orderValueSOL() {
      return this.convertEURtoSOLPretty(this.orderValue); //this.totalValue / 212; // 212 EUR to SOL on 8 Nov
    },
    vat() {
      return this.t.data.order.vat || 0;
    },
    fulfilmentDiscount() {
      return this.t.data.order.fulfilment_discount || 0;
    },
    specialDiscount() {
      return this.t.data.order.special_discount || 0;
    },
    tukDiscount() {
      return this.t.data.order.tuk_discount || 0;
    },
    clientCash() {
      // if (!d.fare) return 0;
      return this.t.data.fare.client || this.t.data.fare.cash; // for backward compatability
    },
    subsidisedDeliveryFee() {
      if (this.t.data.type !== "delivery") return 0;
      // return this.t.data.fare.price - this.t.data.order.delivery_subsidy;
      // return this.t.data.fare.cash - this.t.data.order.delivery_subsidy;
      // return this.t.data.fare.client - this.t.data.order.delivery_subsidy;
      return this.clientCash - this.t.data.order.delivery_subsidy;
    },
    deliveryFee() {
      if (this.t.data.type !== "delivery") return 0;
      var deliveryFee = this.subsidisedDeliveryFee;
      if (this.t.data.delivery.is_free_delivery) return 0;
      return deliveryFee;
    },
    showDeliveryFee() {
      return this.t.data.type === "delivery";
    },
    currency() {
      var country = "th"; // default country
      if (this.t.data.business) country = this.t.data.business.country;
      return this.currencySymbol(country); // should store in order
    },
    isRequestingDriver() {
      if (!this.t.delivery) return false;
      var dt = this.t.delivery; // delivery transaction
      if (!dt.driver && dt.state === "initiated") return true;
      return false;
    },
    isRequestAgain() {
      if (!this.t.delivery) return false;
      var dt = this.t.delivery; // delivery transaction
      if (!dt.driver && dt.state !== "initiated") return true;
      return false;
    },
    simpleState() {
      // if (this.t.state === "timed_out") return "timed-out";
      if (this.t.state === "cancelled_by_shop" || this.t.state === "timed_out")
        return "shop-cancelled";
      if (this.t.state === "cancelled_by_customer") return "customer-cancelled";
      if (this.t.state === "initiated") return "placed";
      if (this.t.delivery) {
        if (this.t.delivery.state === "enroute") return "on-the-way";
        if (this.t.delivery.state === "alighted") return "done";
        if (this.t.state === "ready") return "being-prepared";
        if (this.t.state === "completed") return "being-prepared";
      }
      if (this.t.state === "confirmed") return "being-prepared";
      if (this.t.data.type === "take-away") {
        if (this.t.state === "ready") return "ready-for-pickup";
        if (this.t.state === "completed") return "done";
      }
      if (this.t.state === "completed") return "done";
      // if not a delivery then use other logic
      return "being-prepared";
    },
    googleMapURL() {
      var p = this.t.data.business;
      return `http://www.google.com/maps/place/${p.lat},${p.lon}`;
    },
    hasOutOfStock() {
      return this.basketItems.find((o) => o.out_of_stock);
      // return this.basketItems.find((o) => this.isOOS(o));
    },
  },
  mounted() {
    // this.onVisibilityChange();
    document.addEventListener("visibilitychange", this.reinit);
    this.init();
    this.checkName(); // try to get legacy users to set a name...
    setInterval(() => (this.loadingTimer += 1), 1000);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.reinit);
  },
  // activated() {},
  methods: {
    gotoAlternativeShop() {
      var alternative = this.t.data.alternative_shop;
      if (!alternative) return this.goto(`/eat`);
      if (alternative.startsWith("@")) return this.goto(`/${alternative}`);
      return this.goto(`/shop/${this.t.data.alternative_shop}`);
    },
    showDeliveryPhoto() {
      var pic = this.t.delivery.data.delivery_confirmation_pic;
      var msg = `Delivery Photo
      <p style='margin-top:16px'><img src='${pic}' style='border-radius:8px' /></p>`;
      this.successToast(msg, "bottom", 12000);
    },
    isOOS(o) {
      return o.item.name.startsWith("[Out of Stock]");
    },
    isOriginalOOS(o) {
      var original = o.item.name;
      if (original.startsWith("[Out of Stock]")) return false;
      return this.basketItems.find((o) => {
        return (
          o.item.name.startsWith("[Out of Stock]") &&
          o.item.name.endsWith(original)
        );
      });
    },
    checkName() {
      var user = this.$store.state.user;
      if (!user) return; // just a viewer, not logged in
      if (user.name) return; // already has a name
      setTimeout(() => this.setName(), 5000); // otherwise ask user for name
    },
    paymentCompleteInfo() {
      if (this.isCardPayment) {
        var msg = `<div style='text-align:left'>
        Your card will be charged<br>after the order is completed.<p style='margin-top:12px'>If the order is cancelled<br>you won't be charged.</p>
        </div>`;
        this.infoToast(msg, "top", 8000);
      }
    },
    manageTestOrder() {
      this.goto(`/ecommerce/${this.t.business_id}`);
    },
    openDirectionsToShop() {
      window.open(this.googleMapURL, "_target");
    },
    paymentPendingInfo() {
      // var msg = `Contactless payment details will show<br>after the shop has confirmed the order.<br>You can also pay the driver in cash.`;
      var msg = `Contactless payment details will show<br>after the driver arrives at the shop.<br>You can also pay the driver in cash.`;
      if (this.t.delivery && this.driver)
        msg = `Drivers accept cash and <br>contactless payments:<br>Bank Transfer / PromptPay<br>They'll provide details on arrival`;
      if (this.isNotDelivery)
        msg = `For take-away and dine-in orders<br>please pay at the shop.`;
      this.infoToast(msg, "top", 16000);
      if (this.$store.getters.isSuperUser) this.paymentDebug();
    },
    paymentDebug() {
      var hasDelivery = this.t.delivery ? true : false;
      var recentlyOngoing = this.deliveryRecentlyOngoing ? true : false;
      var hasDriver = this.driver ? true : false;
      var hasPromptPay, hasBankAccount;
      if (hasDelivery) {
        hasPromptPay = this.promptPay ? true : false;
        hasBankAccount = this.bankAccount ? true : false;
      }
      var msg = `<div style='color:black'>hasDelivery ${hasDelivery}<br>hasDriver ${hasDriver}<br>recentlyOngoing ${recentlyOngoing}<br>hasPromptPay ${hasPromptPay}<br>hasBankAccount ${hasBankAccount}</div>`;
      this.warningToast(msg, "bottom", 16000);
    },
    paymentReceiptConfirmation() {
      var pic = this.t.data.payment_receipt_pic;
      var msg = `Payment Receipt Has Been Uploaded
      <p style='margin-top:16px'><img src='${pic}' style='border-radius:8px' /></p>`;
      this.successToast(msg, "bottom", 12000);
    },
    setUpdateTimer() {
      this.timer = setTimeout(() => this.init(), 60 * 1000);
    },
    toggleItemsInfo() {
      this.showPics = !this.showPics;
      this.showDescriptions = !this.showDescriptions;
    },
    hasOptions(o) {
      if (!o.item.options) return false;
      return o.item.options.length;
    },
    triggerHelp() {
      this.showHelp = true;
    },
    callShop() {
      let info = `${this.t.data.business.name} #${this.t.data.ref}`;
      this.log("customer_called_shop", info);
      this.sendSuperuserNotification(`📞🏪 Customer calling shop: ${info}`);
      this.callPhoneNumber(this.t.business.data.phone_number);
    },
    optionMenuName(o) {
      var menu = o.menu.name;
      if (o.menu[this.lang]) menu = o.menu[this.lang];
      return menu;
    },
    optionItemName(o) {
      var option = o.option.name;
      if (o.option[this.lang]) option = o.option[this.lang].name;
      return option;
    },
    faqIsFoodReady() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>Is my order ready?</p>
        <p style='margin-top:12px; font-weight:550; font-size:0.9em; opacity:0.6'>
        For Delivery Orders
        </p>
        <p>The order is ready when it says: <button>Your order is on the way</button></p>
        <p style='font-size:0.7em; opacity:0.6; margin-top:4px'><i>*At that time you'll be able to see the driver on the map.</i></p>
        <p style='margin-top:12px; font-weight:550; font-size:0.9em; opacity:0.6'>
        For Take-away / Self-pickup Orders
        </p>
        <p>The order is ready when it says: <button>Ready for Pickup</button></p>
        <p style='font-size:0.7em; opacity:0.6; margin-top:4px'><i>*Please pickup the order within 15 minutes of it being ready.</i></p>
        </div>`,
        showCloseButton: true,
      });
    },
    faqWhereIsDriver() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>Where is the driver?</p>
        <p style='margin-top:12px'>The driver is waiting at the restaurant until the order is ready.</p>
        <p style='margin-top:10px'>Once the driver is on the way to you with the order you will be able to see them on the map.</p>
        </div>`,
        showCloseButton: true,
      });
    },
    faqIncorrectOrder() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>What if the shop sent the incorrect order?</p>
        <p style='margin-top:12px'>If the shop sent the incorrect order you can call the shop to tell them and they'll fix it for you.</p>
        <p style='margin-top:10px'>If you're having trouble contacting the shop you should message TUK customer support and they'll fix it for you.</p>
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t("call-shop"),
        cancelButtonText: this.$t("close"),
        confirmButtonColor: this.bulmaColor("link"),
      }).then((val) => {
        if (val.value) this.callShop();
      });
    },
    faqCancelOrder() {
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>How can I cancel my order?</p>
        <p style='margin-top:12px'>If the shop has not yet confirmed your order, click the red cancel button.</p>
        <p style='margin-top:10px'>If the shop has already confirmed your order, call them to cancel the order.</p>
        </div>`,
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t("call-shop"),
        cancelButtonText: this.$t("close"),
        confirmButtonColor: this.bulmaColor("link"),
      }).then((val) => {
        if (val.value) this.callShop();
      });
    },
    faqWhatIsShopAddress() {
      var title = this.$t("faq.what-is-shop-address-?");
      var text = this.$t("faq.shop-address-text");
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>${title}</p>
        <p style='margin-top:12px'>${text}</p>
        <p style='margin-top:10px'>
        <a href='${this.googleMapURL}' target='_blank'>Google Map Directions</a>
        </p>
        </div>`,
        showCloseButton: true,
      });
    },
    faqHowDoIPayNotDelivery() {
      var title = this.$t("faq.how-do-i-pay-?");
      var text1 = this.$t("faq.pay-text-1");
      var text2 = this.$t("faq.pay-text-2");
      this.$swal({
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>${title}</p>
        <p style='margin-top:12px'>${text1}</p>
        <p style='margin-top:10px'>${text2}</p>
        </div>`,
        showCloseButton: true,
      });
    },
    // onVisibilityChange() {
    //   document.addEventListener("visibilitychange", () => {
    //     if (document.visibilityState === "visible") this.init();
    //   });
    // },
    goToShop() {
      this.goto(`shop/${this.t.business_id}`);
    },
    copyOrderID() {
      this.$copyText(this.t.id);
      this.infoToast("Order ID copied to clipboard");
    },
    shareOrder() {
      var path = "/commerce_order/" + this.t.id;
      if (this.t.data.short_id) path = "/o/" + this.t.data.short_id;
      var url = window.location.origin + path;
      // var url = window.location.href;
      this.$copyText(url);
      if (!navigator.share) {
        var msg = `<i class='fa fa-copy'></i>&nbsp;Share order link copied!<br>${url}`;
        this.infoToast(msg, "top");
        return;
      }
      var shareMsg = `🛺 Here's your TUK App order for ${this.t.business.name}
👉🏼 ${url}`;
      navigator
        .share({ text: shareMsg })
        .catch((err) => this.console("Error sharing", err));
    },
    copyURL() {
      var path = "/commerce_order/" + this.t.id;
      if (this.t.data.short_id) path = "/o/" + this.t.data.short_id;
      var url = window.location.origin + path;
      this.$copyText(url);
      this.infoToast("Copied Order URL:<br>" + url, "top");
    },
    defaultImg(e) {
      e.target.src = this.$store.state.menuItemPlaceholderPic;
    },
    reinit() {
      if (document.visibilityState === "visible") this.init();
    },
    init() {
      if (this.$route.name !== "commerce-order") return; // try to fix bug
      var id = this.$route.params.id; // this.console(`LogisticsOrder this.$route ${id}`, this.$route);
      if (id) this.getCommerceOrder(id); // fix bug
    },
    getCommerceOrder(id) {
      if (!this.showPayment) {
        this.updating = true;
        // this.transaction = null;
      }
      this.$store
        .dispatch("getCommerceOrder", id)
        .then((res) => {
          this.transaction = res.data; // must be a commerce order
          if (
            this.transaction.is_ongoing ||
            (this.transaction.delivery && this.transaction.delivery.is_ongoing)
          ) {
            if (!this.timer) this.setUpdateTimer();
            else console.log("timer", this.timer);
          } else {
            clearTimeout(this.timer);
          }
        })
        .catch((err) => this.apiError(err))
        .finally(() => {
          this.updating = false;
          this.z += 1;
        });
    },
    showCashbackInfo() {
      this.$swal({
        html: `<div style='text-align: left'>
        <p style="font-size:1.2em">🎉 Happy Songkran!</p>
        <p style="margin-top:10px; font-size:0.9em"><i>You get cashback!</i></p>
        <p style="margin-top:10px; font-size:0.85em; font-weight:500; opacity:0.7">
        <i>Your friendly TUK driver will give you the cashback on arrival with your delivery.</i>
        </p>
        </div>`,
        showCloseButton: true,
        showCancelButton: false,
        showConfirmButton: true,
        confirmButtonText: this.$t("okay"),
        confirmButtonColor: this.bulmaColor("success"),
      });
    },
    prettyOptions(options) {
      return options
        .map((o) => {
          var menu = o.menu.name;
          if (o.menu[this.lang]) menu = o.menu[this.lang];
          var option = o.option.name;
          if (o.option[this.lang]) option = o.option[this.lang].name;
          return `${menu}: ${option}`;
        })
        .join(", ");
    },
    isSpecialItem(o) {
      if (o.item.id && o.item.id.startsWith("~")) return true;
      return false;
    },
    // isDelivery(t) {
    //   // return true only if delivery has a driver assigned..
    //   // return t.delivery; // doesn't show because backend isn't setting delivery transaction
    //   if (t.data.type !== "delivery") return false;
    //   // check if ongoing transactions has a delivery with this commerce transactions id
    //   var txns = this.$store.state.ongoingCustomerTransactions;
    //   if (!txns) return false; // should use getters to guarantee array []
    //   var d = txns.find((txn) => txn.data.commerce_transaction_id === t.id); // this is the delivery txn
    //   if (!d) return false; // no ongoing delivery txn
    //   if (d.state === "initiated") return false; // delivery txn, but no driver yet...
    //   return true;
    // },
    deliveryState(t) {
      if (!t.delivery) return "None";
      var state = t.delivery.state;
      return this.translateState(state);
    },
    fulfilmentIcon(t) {
      if (t.data.type === "take-away") return "fa-walking";
      if (t.data.type === "delivery") return "fa-shipping-fast";
      if (t.data.type === "dine-in") return "fa-users";
      return "fa-question";
    },
    fulfilmentType(t) {
      return this.translateFulfilmentType(t.data.type);
    },
    // init(t) {
    //   this.transaction = t;
    //   //  this.z += 1
    // },
    itemName(o) {
      if (o[this.lang]) {
        return o[this.lang].name;
      }
      return o.name;
    },
    itemDescription(o) {
      if (o[this.lang]) return o[this.lang].description;
      return o.description;
    },
    // prettyState(t) {
    //   if (t.state === "initiated")
    //     return this.$t("ecommerce.waiting-for-confirmation");
    //   if (t.state === "confirmed") return this.$t("ecommerce.being-prepared");
    //   if (t.state === "ready") return this.$t("ecommerce.waiting-for-pickup"); // if take-away
    //   if (t.state === "completed") return this.$t("ecommerce.completed");
    //   if (t.state === "timed_out") return this.$t("ecommerce.expired");
    //   if (t.state === "cancelled_by_shop")
    //     return this.$t("ecommerce.shop-cancelled");
    //   return t.state;
    // },
    // stateClass(t) {
    //   var tagClass = "is-dark";
    //   if (t.state === "initiated") tagClass = "is-danger";
    //   if (t.state === "confirmed") tagClass = "is-warning";
    //   if (t.state === "ready") tagClass = "is-orange";
    //   if (t.state === "completed") tagClass = "is-success";
    //   if (t.state === "cancelled_by_shop") tagClass = "is-danger";
    //   if (t.state === "timed_out") tagClass = "is-danger";
    //   return tagClass;
    // },
    cancelOrder() {
      var red = this.bulmaColor("danger");
      var title = this.$t("ecommerce.cancel-order") + "?";
      this.$swal({
        // title: `<i class="fa fa-question fa-lg" />`,
        html: `<div style='text-align:left'>
        <p style='font-size:1.2em'>
          <i class="fa fa-exclamation-triangle" style='color:${red}'></i>
          &nbsp;${title}
        </p>
        <p style='margin-top:12px'>
        ${this.$t("commerce.cancel-are-you-sure")}
        </p>
        `,
        showCloseButton: true,
        showCancelButton: true,
        cancelButtonText: this.$t("yes-cancel"),
        cancelButtonColor: red,
        confirmButtonText: this.$t("no"),
        confirmButtonColor: this.bulmaColor("success"),
      }).then((result) => {
        this.console("reject result", result);
        if (result.dismiss === "cancel") {
          var loader = this.loadingDots();
          this.$store
            .dispatch("patchTransaction", {
              transactionID: this.t.id,
              data: { state: "cancelled_by_customer" },
            })
            .then(() => {
              this.updateCustomerOrders();
              this.$toast.open("Order cancelled!");
              this.log("order_cancelled_by_customer");
              this.goHome();
            })
            .catch((err) => this.apiError(err))
            .finally(() => loader.hide());
        }
      });
    },
    orderID(t) {
      return t.id.substr(t.id.length - 5);
    },
  },
};
</script>

<style scoped>
.commerce-order {
  /* background-color: white; */
  max-width: 700px;
  margin: auto;
}

.wrapper {
  /* position: absolute; */
  text-align: left;
  padding: 16px 12px;
  top: 0;
  height: 100vh;
  padding-bottom: 40px;
  overflow: scroll;
  max-width: 700px;
  margin: auto;
  background-color: white;
}

.header {
  font-weight: 500;
  font-size: 1.2em;
  color: grey;
  margin-left: 45px;
  padding-bottom: 8px;
}

.total-sol {
  margin-left: 6px;
  background-color: var(--crypto-secondary);
  /* color: var(--crypto-primary); */
  padding: 0 4px;
  border-radius: 3px;
  font-weight: 500;
  color: black;
}

.dark .table {
  background-color: slategrey;
  color: white;
}

.notes {
  /* padding-top: 8px;
  background-color: white; */
  background-color: rgb(245, 245, 245);
  border-radius: 3px;
  margin-top: 4px;
  padding: 5px 0px;
}

.transaction {
  border-radius: 2px;
  box-shadow: 0px 0px 1px grey;
  width: 100%;
  background-color: white;
}

.driver {
  width: 100%;
  margin-top: 8px;
  background-color: lightyellow;
}

.hack {
  text-align: left;
  margin-top: 8px;
  background-color: rgba(144, 238, 144, 0.619);
  /* text-align: center; */
  padding: 6px;
  /* font-style: italic; */
  width: 100%;
  font-weight: 500;
}

.columns {
  padding: 0 0;
  margin: 0 0;
}

.columns:last-child {
  margin-bottom: 0;
}

.column.is-narrow {
  /* margin: 0; */
  padding: 0;
  /* margin-top: 10px; */
  margin-right: 10px;
}

.tags {
  margin-bottom: 0;
}

.tag {
  padding: 0 6px;
}

/* .tags .tag {
  margin-bottom: 0;
} */
.pad {
  display: inline-block;
  width: 25px;
  /* text-align: center; */
  /* margin-right: 8px; */
  color: grey;
}

p {
  margin-bottom: 0px !important;
}

.table {
  background-color: white;
  /* background-color: rgb(245, 245, 245); */
  border-radius: 2px;
  margin-top: 10px;
  /* box-shadow: 0px 0px 2px 1px lightgrey; */
  box-shadow: 0 0 1px grey;
}

td,
th {
  /* padding: 0; */
  padding: 6px;
}

thead th {
  opacity: 0.5;
}

.out-of-stock {
  background-color: var(--danger);
  /* margin-left: -6px; */
  padding: 3px 6px;
  font-weight: 550;
  color: white;
  border-radius: 3px;
}

.contain-img {
  object-fit: contain !important;
}


img {
  /* height: 80px;
  width: auto; */
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
  object-fit: cover;
  /* height: 90px; */
  height: 90px;
  width: 90px;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.1);
  margin: 0;
  margin-bottom: -7.5px;
}

.item-img {
  border-radius: 3px;
  object-fit: cover;
  height: 120px;
  width: 120px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.14);
  background-color: white;
}

.description {
  /* background-color: lightyellow; */
  padding: 2px;
  border-radius: 4px;
}

.mitten-wrapper {
  padding: 6px;
  margin-left: 6px;
  border: 1px solid rgba(128, 128, 128, 0.6);
  opacity: 0.7;
}

.no-driver {
  text-align: left;
  padding: 6px;
  /* font-style: italic; */
  width: 100%;
  color: grey;
  font-weight: 450;
}

.is-orange {
  background-color: orange;
}

.payment {
  /* background-color: snow; */
  padding: 0 4px;
  border-radius: 4px;
  margin-left: 4px;
  font-weight: 500;
}

/* .options {
  font-size: 0.9em;
  background-color: rgba(224, 255, 255, 0.7);
  padding: 2px;
  border-radius: 3px;
} */
.special-item-positive {
  color: green;
}

.special-item-negative {
  color: red;
}

.oos-item {
  background-color: #fee;
  color: #aaa;
}

.state-bars .column {
  padding: 6px;
  margin-bottom: 10px;
}

.state-icon {
  font-size: 6em;
}

.map-btn {
  width: 100%;
  font-weight: 550;
  text-shadow: 0 0 3px white, 0 0 1px white, 0 0 4px white;
  margin-bottom: 20px;
  background-image: url("~@/assets/map_button_background.png");
  background-size: cover;
  background-position: top;
}

.map-btn:hover {
  color: blue !important;
}

.pay-btn {
  width: 100%;
  font-weight: 450;
  text-shadow: 0 0 3px black, 0 0 1px black, 0 0 4px black;
  margin-bottom: 20px;
  background-image: url("~@/assets/matrix-btn.png");
  background-size: cover;
  background-position: top;
}

.pay-btn:hover {
  color: yellow !important;
}

.timeout-option {
  text-align: left;
  font-size: 0.8;
  font-weight: 450;
  /* opacity: 0.6; */
  color: grey;
  padding: 1px 4px;
  /* border: 1px solid grey; */
  box-shadow: 0 0 1px grey;
  margin-top: 6px;
  border-radius: 2px;
}

.faq {
  margin: 40px 0;
  color: grey;
  font-size: 0.9em;
}

.list-title {
  font-weight: 500;
  padding-bottom: 6px;
}

.list-item {
  margin: 3px 0;
  padding: 9px;
  background-color: white;
}

.options {
  /* background-color: rgba(255, 255, 0, 0.6); */
  padding: 2px;
  border-radius: 3px;
  /* margin-left: -6px; */
  /* width: calc(100% + 12px); */
  font-size: 0.9em;
  /* background-color: rgb(235, 235, 235); */
  background-color: rgb(250, 250, 245);
}

.td-options {
  padding-top: 0;
}

.options td {
  border-bottom: 1pt solid black;
  padding: 2px 6px;
}

.option-menu {
  font-weight: 500;
  opacity: 0.5;
  font-size: 0.9em;
}

.no-bottom-border td {
  border-bottom: none;
  padding-bottom: 0;
}

tbody.items {
  width: 100%;
  border-bottom: 1px solid #eee;
}

thead th {
  border-bottom: 0px;
  padding-bottom: 8px;
}

.more-btn {
  margin-left: 6px;
  margin-right: 12px;
  padding: 2px;
  line-height: 1.2em;
  border-bottom: 1px dotted #555;
}

.discount-tag {
  opacity: 0.7;
  font-size: 0.7em;
  margin-left: 2px;
}

.generic-order {
  position: absolute;
  top: 0;
  left: 0;
  margin: 10px;
  padding: 8px;
  color: #bbb;
}

.fail-divider {
  background-color: white;
  width: 100%;
  height: 2px;
  margin-bottom: 12px;
}

.test-order {
  color: rgba(255, 0, 0, 0.7);
  font-weight: 400;
  background-color: rgb(247, 247, 247);
  border-radius: 12px;
  border: 1px solid #ccc;
  font-size: 0.9em;
  padding: 2px 5px;
  /* position: absolute;
  left: 53px;
  top: 46px; */
  float: right;
}

.lottie {
  width: 200px;
  height: 200px;
  margin: auto;
  /* border: 1px solid yellow; */
}

.call-shop-prompt {
  background-color: whitesmoke;
  border: 1px solid lightgrey;
  padding: 6px 8px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.call-shop-prompt.yellow {
  background-color: lightgoldenrodyellow;
}

.call-shop-prompt.blue {
  background-color: rgb(196, 232, 255);
}

.bg-pink {
  background-color: var(--danger);
  color: white;
  border-radius: 3px;
  font-size: 1.2em;
  padding: 6px;
  font-weight: 500;
}

.nowrap {
  white-space: nowrap;
}

.blocked-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 20px;
  height: 50vh;
  min-height: 300px;
  background-color: black;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  box-shadow: 0 0 4px grey;
  color: white;
}

.blocked-wrapper>p {
  padding-bottom: 20px;
  font-size: 1.2em;
}
</style>